import React, { useState, useEffect, useMemo } from 'react';
import {
  FileSpreadsheet,
  Download,
  Calendar,
  TrendingUp,
  DollarSign,
  Users,
  FileText,
  BarChart3,
  Filter,
  Loader2,
  Check,
  X,
  Info,
} from 'lucide-react';
import { subscribeToBookings } from '../../services/firestore';
import { formatCurrency } from '../../types/booking';
import * as XLSX from 'xlsx';

const ReportsAndExports = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [selectedReportType, setSelectedReportType] = useState('all');

  useEffect(() => {
    const unsubscribe = subscribeToBookings((data) => {
      setBookings(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Filter bookings by date range
  const filteredBookings = useMemo(() => {
    return bookings.filter((booking) => {
      const bookingDate = booking.eventDate?.toDate
        ? booking.eventDate.toDate()
        : new Date(booking.eventDate);
      const bookingDateStr = bookingDate.toISOString().split('T')[0];
      return bookingDateStr >= dateRange.start && bookingDateStr <= dateRange.end;
    });
  }, [bookings, dateRange]);

  // Calculate statistics
  const stats = useMemo(() => {
    const confirmed = filteredBookings.filter((b) => b.status === 'COMPLETED');
    const pending = filteredBookings.filter((b) => b.status === 'PENDING_DEPOSIT');
    const cancelled = filteredBookings.filter((b) => b.status === 'CANCELLED');

    const totalRevenue = confirmed.reduce((sum, b) => sum + (b.totalAmount || 0), 0);
    const totalDeposits = confirmed.reduce((sum, b) => sum + (b.depositAmount || 0), 0);
    const pendingDeposits = pending.reduce((sum, b) => sum + (b.depositAmount || 0), 0);

    return {
      totalBookings: filteredBookings.length,
      completedBookings: confirmed.length,
      pendingBookings: pending.length,
      cancelledBookings: cancelled.length,
      totalRevenue,
      totalDeposits,
      pendingDeposits,
      averageOrderValue: confirmed.length > 0 ? totalRevenue / confirmed.length : 0,
    };
  }, [filteredBookings]);

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Export to Excel/CSV
  const exportToExcel = (exportType = 'all') => {
    setExporting(true);
    try {
      let data = [];
      let filename = '';

      if (exportType === 'all' || exportType === 'bookings') {
        // Export all bookings
        data = filteredBookings.map((b) => {
          const eventDate = b.eventDate?.toDate
            ? b.eventDate.toDate()
            : new Date(b.eventDate);
          return {
            'Booking Number': b.bookingNumber,
            'Client Name': b.clientName,
            'Email': b.clientEmail || '',
            'Phone': b.clientPhone || '',
            'Event Date': eventDate.toLocaleDateString('id-ID'),
            'Event Time': b.eventTime || '',
            'Event Type': b.eventType,
            'Location': b.eventLocation,
            'Status': b.status,
            'Total Amount': b.totalAmount || 0,
            'Deposit Amount': b.depositAmount || 0,
            'Deposit Percentage': b.depositPercent || 30,
            'Remaining Amount': b.remainingAmount || 0,
            'Payment Method': b.paymentMethod || '',
            'Payment Status': b.paymentStatus || 'PENDING',
            'Created At': b.createdAt?.toDate
              ? b.createdAt.toDate().toLocaleDateString('id-ID')
              : '',
          };
        });
        filename = `bookings_${dateRange.start}_to_${dateRange.end}`;
      } else if (exportType === 'revenue') {
        // Export revenue report
        const monthlyRevenue = {};

        filteredBookings.forEach((b) => {
          if (b.status !== 'COMPLETED') return;

          const eventDate = b.eventDate?.toDate
            ? b.eventDate.toDate()
            : new Date(b.eventDate);
          const monthKey = `${eventDate.getFullYear()}-${String(
            eventDate.getMonth() + 1
          ).padStart(2, '0')}`;

          if (!monthlyRevenue[monthKey]) {
            monthlyRevenue[monthKey] = {
              Month: monthKey,
              'Total Bookings': 0,
              'Completed Bookings': 0,
              Revenue: 0,
              Deposits: 0,
            };
          }

          monthlyRevenue[monthKey]['Total Bookings']++;
          monthlyRevenue[monthKey]['Completed Bookings']++;
          monthlyRevenue[monthKey].Revenue += b.totalAmount || 0;
          monthlyRevenue[monthKey].Deposits += b.depositAmount || 0;
        });

        data = Object.values(monthlyRevenue).sort((a, b) =>
          a.Month.localeCompare(b.Month)
        );
        filename = `revenue_report_${dateRange.start}_to_${dateRange.end}`;
      } else if (exportType === 'clients') {
        // Export client list
        const clientMap = new Map();

        filteredBookings.forEach((b) => {
          if (!clientMap.has(b.clientEmail)) {
            clientMap.set(b.clientEmail, {
              'Client Name': b.clientName,
              'Email': b.clientEmail || '',
              'Phone': b.clientPhone || '',
              'Instagram': b.clientInstagram || '',
              'Total Bookings': 0,
              'Total Spent': 0,
              'Last Booking': '',
            });
          }

          const client = clientMap.get(b.clientEmail);
          client['Total Bookings']++;
          if (b.status === 'COMPLETED') {
            client['Total Spent'] += b.totalAmount || 0;
          }

          const eventDate = b.eventDate?.toDate
            ? b.eventDate.toDate()
            : new Date(b.eventDate);
          const dateStr = eventDate.toLocaleDateString('id-ID');

          if (!client['Last Booking'] || eventDate > new Date(client['Last Booking'])) {
            client['Last Booking'] = dateStr;
          }
        });

        data = Array.from(clientMap.values());
        filename = `clients_${dateRange.start}_to_${dateRange.end}`;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Set column widths
      const colWidths = Object.keys(data[0] || {}).map(() => ({ wch: 20 }));
      worksheet['!cols'] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

      // Generate file
      XLSX.writeFile(workbook, `${filename}.xlsx`);

      alert(`Export berhasil: ${filename}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Gagal melakukan export. Silakan coba lagi.');
    } finally {
      setExporting(false);
    }
  };

  // Export to CSV
  const exportToCSV = (exportType = 'all') => {
    setExporting(true);
    try {
      let data = [];
      let filename = '';

      if (exportType === 'all' || exportType === 'bookings') {
        data = filteredBookings.map((b) => ({
          bookingNumber: b.bookingNumber,
          clientName: b.clientName,
          clientEmail: b.clientEmail || '',
          clientPhone: b.clientPhone || '',
          eventDate: b.eventDate?.toDate
            ? b.eventDate.toDate().toLocaleDateString('id-ID')
            : '',
          eventTime: b.eventTime || '',
          eventType: b.eventType,
          eventLocation: b.eventLocation,
          status: b.status,
          totalAmount: b.totalAmount || 0,
          depositAmount: b.depositAmount || 0,
          paymentMethod: b.paymentMethod || '',
        }));
        filename = `bookings_${dateRange.start}_to_${dateRange.end}`;
      } else if (exportType === 'revenue') {
        const monthlyRevenue = {};

        filteredBookings.forEach((b) => {
          if (b.status !== 'COMPLETED') return;

          const eventDate = b.eventDate?.toDate
            ? b.eventDate.toDate()
            : new Date(b.eventDate);
          const monthKey = `${eventDate.getFullYear()}-${String(
            eventDate.getMonth() + 1
          ).padStart(2, '0')}`;

          if (!monthlyRevenue[monthKey]) {
            monthlyRevenue[monthKey] = {
              month: monthKey,
              bookings: 0,
              revenue: 0,
              deposits: 0,
            };
          }

          monthlyRevenue[monthKey].bookings++;
          monthlyRevenue[monthKey].revenue += b.totalAmount || 0;
          monthlyRevenue[monthKey].deposits += b.depositAmount || 0;
        });

        data = Object.values(monthlyRevenue).sort((a, b) =>
          a.month.localeCompare(b.month)
        );
        filename = `revenue_report_${dateRange.start}_to_${dateRange.end}`;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

      // Generate CSV file
      XLSX.writeFile(workbook, `${filename}.csv`);

      alert(`Export berhasil: ${filename}.csv`);
    } catch (error) {
      console.error('Export error:', error);
      alert('Gagal melakukan export. Silakan coba lagi.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF7A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#F7F5F2]">Laporan & Export</h2>
          <p className="text-[#F7F5F2]/70 text-sm mt-1">
            Download laporan booking, pendapatan, dan data klien
          </p>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={18} className="text-[#D4AF7A]" />
          <h3 className="font-semibold text-[#F7F5F2]">Filter Tanggal</h3>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-[#F7F5F2]/70 mb-2">Tanggal Mulai</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#F7F5F2]/70 mb-2">Tanggal Akhir</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] focus:outline-none focus:border-[#D4AF7A]"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() =>
                setDateRange({
                  start: new Date(new Date().setMonth(new Date().getMonth() - 1))
                    .toISOString()
                    .split('T')[0],
                  end: new Date().toISOString().split('T')[0],
                })
              }
              className="w-full px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20"
            >
              Reset ke Default
            </button>
          </div>
        </div>

        <div className="text-sm text-[#F7F5F2]/60 mt-3">
          Menampilkan data dari {formatDateDisplay(dateRange.start)} sampai{' '}
          {formatDateDisplay(dateRange.end)} ({filteredBookings.length} booking)
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Total Booking</p>
              <p className="text-3xl font-bold text-[#F7F5F2] mt-1">
                {stats.totalBookings}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="text-sm text-[#F7F5F2]/60">
            {stats.completedBookings} selesai • {stats.pendingBookings} pending
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Total Pendapatan</p>
              <p className="text-3xl font-bold text-[#D4AF7A] mt-1">
                {formatCurrency(stats.totalRevenue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="text-sm text-[#F7F5F2]/60">
            Dari {stats.completedBookings} booking selesai
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Deposit Terkumpul</p>
              <p className="text-3xl font-bold text-[#D4AF7A] mt-1">
                {formatCurrency(stats.totalDeposits)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <div className="text-sm text-[#F7F5F2]/60">
            Pending: {formatCurrency(stats.pendingDeposits)}
          </div>
        </div>

        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-[#F7F5F2]/70">Rata-rata Order</p>
              <p className="text-3xl font-bold text-[#F7F5F2] mt-1">
                {formatCurrency(stats.averageOrderValue)}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-orange-400" />
            </div>
          </div>
          <div className="text-sm text-[#F7F5F2]/60">
            Per booking selesai
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Booking Export */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-[#F7F5F2]">Export Booking</h3>
              <p className="text-sm text-[#F7F5F2]/60">Semua data booking</p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={() => exportToExcel('bookings')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export ke Excel (.xlsx)
            </button>
            <button
              onClick={() => exportToCSV('bookings')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export ke CSV
            </button>
          </div>
        </div>

        {/* Revenue Report Export */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-[#F7F5F2]">Laporan Pendapatan</h3>
              <p className="text-sm text-[#F7F5F2]/60">Ringkasan bulanan</p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={() => exportToExcel('revenue')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export ke Excel (.xlsx)
            </button>
            <button
              onClick={() => exportToCSV('revenue')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export ke CSV
            </button>
          </div>
        </div>

        {/* Client List Export */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-[#F7F5F2]">Daftar Klien</h3>
              <p className="text-sm text-[#F7F5F2]/60">Data pelanggan</p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={() => exportToExcel('clients')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-lg text-[#D4AF7A] hover:bg-[#D4AF7A]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export ke Excel (.xlsx)
            </button>
            <button
              onClick={() => exportToCSV('clients')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export ke CSV
            </button>
          </div>
        </div>

        {/* Complete Report */}
        <div className="bg-white/5 border border-[#D4AF7A]/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold text-[#F7F5F2]">Laporan Lengkap</h3>
              <p className="text-sm text-[#F7F5F2]/60">Semua data dalam satu file</p>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={() => exportToExcel('all')}
              disabled={exporting || filteredBookings.length === 0}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Export Laporan (.xlsx)
            </button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#F7F5F2]/80">
            <p className="font-medium text-blue-400 mb-1">Informasi Export</p>
            <p>
              File Excel (.xlsx) berisi semua data dengan format yang rapi. File CSV dapat dibuka di
              berbagai aplikasi spreadsheet seperti Microsoft Excel, Google Sheets, atau Numbers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsAndExports;
